/**
 * useEsocialCodigos — hook + cache para tabelas paramétricas eSocial.
 *
 * Faz UMA chamada bulk a `GET /api/esocial-codigos` (todos ativos),
 * cacheia em memória pelo tempo de vida da página, e expõe `getCodigos(cat)`.
 *
 * Fallback: se a API falhar, usa o objeto hardcoded de `esocialCodigos.js`.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import * as DEFAULTS from './esocialCodigos';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

let _cache = null;          // { categoria -> [{codigo, descricao, ativo, custom, ordem, id}] }
let _inflight = null;       // Promise em andamento (evita disparo duplo)

const fallbackFor = (categoria) => {
  const arr = DEFAULTS[categoria];
  if (!Array.isArray(arr)) return [];
  return arr.map((it, i) => ({ ...it, ativo: true, custom: false, ordem: i }));
};

async function _fetch() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await axios.get(`${API}/api/esocial-codigos`, auth());
      const grouped = {};
      (r.data || []).forEach(c => {
        if (!grouped[c.categoria]) grouped[c.categoria] = [];
        grouped[c.categoria].push(c);
      });
      _cache = grouped;
      return grouped;
    } catch {
      // Não cacheia falhas — próximo uso tenta de novo
      const fb = {};
      Object.keys(DEFAULTS).forEach(k => {
        if (Array.isArray(DEFAULTS[k])) fb[k] = fallbackFor(k);
      });
      return fb;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/** Limpa o cache (chamar após CRUD da página de admin). */
export function invalidateEsocialCodigos() {
  _cache = null;
}

/** Hook que entrega lista de uma categoria. */
export function useCodigos(categoria) {
  const [items, setItems] = useState(() => fallbackFor(categoria));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    _fetch().then(grouped => {
      if (cancelled) return;
      const arr = grouped[categoria];
      if (Array.isArray(arr) && arr.length > 0) {
        setItems(arr.filter(i => i.ativo !== false));
      } else {
        setItems(fallbackFor(categoria));
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [categoria]);

  return { items, loading };
}

/** Versão imperativa quando é mais conveniente que hook. */
export async function getCodigos(categoria) {
  const grouped = await _fetch();
  const arr = grouped[categoria];
  return (Array.isArray(arr) && arr.length > 0)
    ? arr.filter(i => i.ativo !== false)
    : fallbackFor(categoria);
}

/** Recarrega tudo (admin → após criar/editar/inativar). */
export async function refreshEsocialCodigos() {
  invalidateEsocialCodigos();
  return await _fetch();
}
